import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { IdName } from '../../work-restrictions-rules.interfaces';

type DateTimePickerDateObj = {
  dateMillis: number | null;
  dateFormatted: string | null;
  validationError: boolean | null;
};

type WeekdayItem = {
  id: number;
  short: string;
  checked: boolean;
};

type WorkRestrictionRule = {
  id: string;
  district: string;
  workType: string;
  weekDays: string;
  timeInterval: string;
  ruleType: string;
  validity: string;
  status: 'Активно' | 'Неактивно';
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-create-work-restriction-rule-modal',
  templateUrl: './create-work-restriction-rule-modal.component.html',
  styleUrls: ['./create-work-restriction-rule-modal.component.scss'],
  standalone: false,
})
export class CreateWorkRestrictionRuleModalComponent implements OnInit {
  @Input() sectorTitle: string;
  @Input() isEditMode = false;
  @Input() editRule: WorkRestrictionRule | null = null;

  // Районы передаются из родительского компонента
  @Input() districts: IdName[] = [];

  // Типы работ передаются из родительского компонента
  @Input() workTypes: IdName[] = [];
  @Input() initialDistrictIds: number[] = [];
  @Input() initialWorkTypeIds: number[] = [];

  selectedDistricts: IdName[] = [];
  selectedWorkTypes: IdName[] = [];

  ruleType: 'schedule' | 'day' = 'schedule';
  validityType: 'forever' | 'period' = 'forever';

  weekdays: WeekdayItem[] = [
    { id: 1, short: 'Пн', checked: false },
    { id: 2, short: 'Вт', checked: false },
    { id: 3, short: 'Ср', checked: false },
    { id: 4, short: 'Чт', checked: false },
    { id: 5, short: 'Пт', checked: false },
    { id: 6, short: 'Сб', checked: false },
    { id: 7, short: 'Вс', checked: false },
  ];

  timeFrom = '18:00';
  timeTo = '23:59';

  periodFrom: DateTimePickerDateObj = { dateMillis: null, dateFormatted: null, validationError: null };
  periodTo: DateTimePickerDateObj = { dateMillis: null, dateFormatted: null, validationError: null };
  periodFromInitDate: Date | null = null;
  periodToInitDate: Date | null = null;

  showDeleteConfirmation = false;

  // Сохраняем начальное состояние для проверки изменений
  private initialState: {
    districtIds: number[];
    workTypeIds: number[];
    ruleType: 'schedule' | 'day';
    weekdays: boolean[];
    timeFrom: string;
    timeTo: string;
    validityType: 'forever' | 'period';
    periodFromMillis: number | null;
    periodToMillis: number | null;
  } | null = null;

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    if (this.isEditMode) {
      this.initFromEditRule();
    } else {
      this.selectedDistricts = [];
      this.selectedWorkTypes = [];
    }
  }

  private initFromEditRule(): void {
    if (!this.editRule) return;

    // Инициализация районов
    if (this.initialDistrictIds && this.initialDistrictIds.length > 0) {
      this.selectedDistricts = this.districts.filter((d) => this.initialDistrictIds.includes(typeof d.id === 'string' ? parseInt(d.id, 10) : d.id));
    } else {
      const districtNames = this.editRule.district.split(', ').map((d) => d.trim());
      this.selectedDistricts = this.districts.filter((d) => districtNames.includes(d.name));
    }

    // Инициализация типов работ
    if (this.initialWorkTypeIds && this.initialWorkTypeIds.length > 0) {
      this.selectedWorkTypes = this.workTypes.filter((w) => this.initialWorkTypeIds.includes(typeof w.id === 'string' ? parseInt(w.id, 10) : w.id));
    } else {
      const workTypeNames = this.editRule.workType.split(', ').map((w) => w.trim());
      this.selectedWorkTypes = this.workTypes.filter((w) => workTypeNames.includes(w.name));
    }

    // Тип правила
    this.ruleType = this.editRule.ruleType === 'День-в-день' || this.editRule.ruleType === 'День в день' ? 'day' : 'schedule';

    // Парсим дни недели
    const dayMap: { [key: string]: number } = {
      Пн: 1,
      Вт: 2,
      Ср: 3,
      Чт: 4,
      Пт: 5,
      Сб: 6,
      Вс: 7,
    };
    const selectedDays = this.editRule.weekDays.split(', ').map((d) => dayMap[d.trim()]);
    this.weekdays.forEach((day) => {
      day.checked = selectedDays.includes(day.id);
    });

    // Парсим временной интервал
    const timeParts = this.editRule.timeInterval.split('—').map((t) => t.trim());
    if (timeParts.length === 2) {
      this.timeFrom = timeParts[0];
      this.timeTo = timeParts[1];
    }

    // Парсим срок действия
    if (this.editRule.validity === 'Бессрочно') {
      this.validityType = 'forever';
    } else {
      this.validityType = 'period';
      const dateParts = this.editRule.validity.split('—').map((d) => d.trim());
      if (dateParts.length === 2) {
        const fromParts = dateParts[0].split('.');
        const toParts = dateParts[1].split('.');
        if (fromParts.length === 3) {
          this.periodFromInitDate = new Date(+fromParts[2], +fromParts[1] - 1, +fromParts[0]);
          this.periodFrom = { dateMillis: this.periodFromInitDate.getTime(), dateFormatted: dateParts[0], validationError: null };
        }
        if (toParts.length === 3) {
          this.periodToInitDate = new Date(+toParts[2], +toParts[1] - 1, +toParts[0]);
          this.periodTo = { dateMillis: this.periodToInitDate.getTime(), dateFormatted: dateParts[1], validationError: null };
        }
      }
    }

    // Сохраняем начальное состояние для проверки изменений
    this.saveInitialState();
  }

  private saveInitialState(): void {
    this.initialState = {
      districtIds: this.selectedDistricts.map((d) => (typeof d.id === 'string' ? parseInt(d.id, 10) : d.id)),
      workTypeIds: this.selectedWorkTypes.map((w) => (typeof w.id === 'string' ? parseInt(w.id, 10) : w.id)),
      ruleType: this.ruleType,
      weekdays: this.weekdays.map((d) => d.checked),
      timeFrom: this.timeFrom,
      timeTo: this.timeTo,
      validityType: this.validityType,
      periodFromMillis: this.periodFrom.dateMillis,
      periodToMillis: this.periodTo.dateMillis,
    };
  }

  get hasChanges(): boolean {
    // В режиме создания всегда разрешаем сохранение (если форма валидна)
    if (!this.isEditMode || !this.initialState) {
      return true;
    }

    const currentDistrictIds = this.selectedDistricts.map((d) => (typeof d.id === 'string' ? parseInt(d.id, 10) : d.id));
    const currentWorkTypeIds = this.selectedWorkTypes.map((w) => (typeof w.id === 'string' ? parseInt(w.id, 10) : w.id));
    const currentWeekdays = this.weekdays.map((d) => d.checked);

    // Сравниваем массивы районов
    const districtsChanged = currentDistrictIds.length !== this.initialState.districtIds.length || !currentDistrictIds.every((id) => this.initialState.districtIds.includes(id));

    // Сравниваем массивы типов работ
    const workTypesChanged = currentWorkTypeIds.length !== this.initialState.workTypeIds.length || !currentWorkTypeIds.every((id) => this.initialState.workTypeIds.includes(id));

    // Сравниваем дни недели
    const weekdaysChanged = !currentWeekdays.every((checked, i) => checked === this.initialState.weekdays[i]);

    return (
      districtsChanged ||
      workTypesChanged ||
      this.ruleType !== this.initialState.ruleType ||
      weekdaysChanged ||
      this.timeFrom !== this.initialState.timeFrom ||
      this.timeTo !== this.initialState.timeTo ||
      this.validityType !== this.initialState.validityType ||
      this.periodFrom.dateMillis !== this.initialState.periodFromMillis ||
      this.periodTo.dateMillis !== this.initialState.periodToMillis
    );
  }

  get isFormValid(): boolean {
    const hasDistricts = this.selectedDistricts && this.selectedDistricts.length > 0;
    const hasWorkTypes = this.selectedWorkTypes && this.selectedWorkTypes.length > 0;
    const hasWeekdays = this.weekdays.some((d) => d.checked);
    const hasValidTime = this.isTimeValid(this.timeFrom) && this.isTimeValid(this.timeTo);
    const hasPeriod = this.validityType === 'forever' || !!(this.periodFrom.dateMillis && this.periodTo.dateMillis);
    return !!(hasDistricts && hasWorkTypes && hasWeekdays && hasValidTime && hasPeriod);
  }

  isTimeValid(time: string): boolean {
    if (!time) return false;
    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    return timeRegex.test(time);
  }

  get timeError(): string | null {
    if (!this.timeFrom && !this.timeTo) return null;
    if (this.timeFrom && !this.isTimeValid(this.timeFrom)) {
      return 'Неверный формат времени. Используйте ЧЧ:ММ (например, 18:00)';
    }
    if (this.timeTo && !this.isTimeValid(this.timeTo)) {
      return 'Неверный формат времени. Используйте ЧЧ:ММ (например, 23:59)';
    }
    return null;
  }

  onPeriodFromChange(dateObj: DateTimePickerDateObj) {
    this.periodFrom = dateObj;
    this.periodFromInitDate = dateObj && dateObj.dateMillis ? new Date(dateObj.dateMillis) : null;
  }

  onPeriodToChange(dateObj: DateTimePickerDateObj) {
    this.periodTo = dateObj;
    this.periodToInitDate = dateObj && dateObj.dateMillis ? new Date(dateObj.dateMillis) : null;
  }

  onDistrictsChange(items: IdName[]): void {
    this.selectedDistricts = items;
  }

  onWorkTypesChange(items: IdName[]): void {
    this.selectedWorkTypes = items;
  }

  onDelete(): void {
    this.showDeleteConfirmation = true;
  }

  onConfirmDelete(): void {
    if (this.editRule) {
      this.activeModal.close({ action: 'delete', ruleId: this.editRule.id });
    }
  }

  onCancelDelete(): void {
    this.showDeleteConfirmation = false;
  }

  onSave() {
    if (!this.isFormValid) return;

    this.activeModal.close({
      action: this.isEditMode ? 'update' : 'create',
      ruleId: this.isEditMode && this.editRule ? this.editRule.id : null,
      sectorTitle: this.sectorTitle,
      selectedDistricts: this.selectedDistricts,
      selectedWorkTypes: this.selectedWorkTypes,
      ruleType: this.ruleType,
      weekdays: this.weekdays.filter((d) => d.checked).map((d) => d.id),
      timeFrom: this.timeFrom,
      timeTo: this.timeTo,
      validityType: this.validityType,
      periodFrom: this.periodFrom,
      periodTo: this.periodTo,
    });
  }
}
